import {
  useState,
  useRef,
  useEffect,
  Fragment,
  forwardRef,
  useImperativeHandle,
} from "react";
// import { FaTruckMoving } from "react-icons/fa";

import { cn } from "../../helpers/cn";

import LoadingSpinner from "../LoadingSpinner";
import DefaultBottomToolbar from "./DefaultBottomToolbar";
import useDataTable from "./private/useDataTable";
import TableTopToolbar from "./private/TableTopToolbar";
import SortArrow from "../Utility/SortArrow";
import { emails } from "../../constants";

// const useDelayedUnmount = (isMounted, delayTime = 1000) => {
//   const [shouldRender, setShouldRender] = useState(false);

//   useEffect(() => {
//     let timeoutId;

//     if (isMounted && !shouldRender) {
//       setShouldRender(true);
//     } else if (!isMounted && shouldRender) {
//       timeoutId = setTimeout(() => setShouldRender(false), delayTime);
//     }

//     return () => clearTimeout(timeoutId);
//   }, [isMounted, delayTime, shouldRender]);

//   return shouldRender;
// };

function BodyRowResponse({ children }) {
  return (
    <tr className="tw-border-b tw-border-muted tw-transition-all tw-h-72 tw-text-center tw-text-lg">
      <td className="tw-p-2 tw-text-base" colSpan={100}>
        {children}
      </td>
    </tr>
  );
}

function DataTableBodyRows({
  instance,
  isLoading = false,
  isNetworkRequestTakingTooLong = false,
  isError = false,
  customErrorMessage = "",
  renderDetailPanel,
  noDataMessage,
}) {
  const { page, prepareRow, visibleColumns } = instance;

  if (isNetworkRequestTakingTooLong) {
    return (
      <BodyRowResponse>
        <span className="tw-flex tw-justify-center tw-flex-col tw-items-center">
          <LoadingSpinner className="tw-h-8 tw-w-8" />
          <span className="tw-text-lg tw-font-medium tw-text-sky-600">
            We are experiencing high traffic - load times may take longer than
            usual.
            <br />
            Thank you for your patience!
          </span>
        </span>
      </BodyRowResponse>
    );
  }

  if (isLoading) {
    return (
      <BodyRowResponse>
        <span className="tw-flex tw-justify-center tw-flex-col tw-items-center">
          <LoadingSpinner className="tw-h-8 tw-w-8" />
          <span className="tw-text-lg">Loading...</span>
        </span>
      </BodyRowResponse>
    );
  }

  if (isError) {
    const message =
      customErrorMessage ||
      "An error occurred while fetching data. Please try again.";

    return (
      <BodyRowResponse>
        <span className="tw-text-red-600 tw-font-medium">{message}</span>

        <div className="tw-text-red-600 tw-font-medium">
          If you believe this shouldn't happen, please contact support with the
          error details and steps to reproduce. We apologize for the
          inconvenience.
        </div>
        <div className="tw-text-red-600 tw-font-medium">
          Email Support: {emails?.supportEmail}
        </div>
      </BodyRowResponse>
    );
  }

  if (page.length === 0) {
    return (
      <BodyRowResponse>
        <span className="tw-font-semibold">
          {noDataMessage || "No results found"}
        </span>
      </BodyRowResponse>
    );
  }

  const cellRenderer = (cell) => {
    if (cell.isAggregated) return cell.render("Aggregated");

    return cell.isPlaceholder ? null : cell.render("Cell");
  };

  return page.map((row) => {
    prepareRow(row);

    const { isSelected } = row;

    return (
      <Fragment key={row.id}>
        <tr
          className={cn(
            "tw-border-b tw-border-t-0 tw-border-x-0 tw-border-solid tw-border-muted last:tw-border-0 hover:tw-bg-muted",
            {
              "tw-bg-sky-200": isSelected,
            },
          )}
          {...row.getRowProps()}
        >
          {row.cells.map((cell) => (
            <td
              {...cell.getCellProps()}
              key={cell.column.id}
              className="tw-p-2 multi-line"
            >
              {cellRenderer(cell)}
            </td>
          ))}
        </tr>

        {row?.isExpanded && typeof renderDetailPanel === "function" && (
          <tr>
            <td className="tw-pt-5" colSpan={visibleColumns.length}>
              {renderDetailPanel({ row })}
            </td>
          </tr>
        )}
      </Fragment>
    );
  });
}

/**
 * @template T
 * @param {DataTableProps<T>} props
 */

function DataTable(
  {
    columns,
    data,
    totalRows = 0,
    enableRowSelection = false,
    enableDetailPanel = false,
    className,

    isLoading = false,
    isError = false,
    isFetching = false,
    customErrorMessage,
    isNetworkRequestTakingTooLong = false,

    initialState,
    slotProps = {
      topToolbar: {
        disabled: false,
      },
      body: {
        noDataMessage: "No data available",
      },
    },

    // components
    renderDetailPanel,
    renderTopToolbar,
    renderWithTopToolbar,
    expandedFirstRowEvent,
    onColumnFiltersChange,
    onPaginationChange,
    onSortingChange,
    ...rest
  },
  ref,
) {
  const instance = useDataTable({
    data,
    columns,
    enableRowSelection,
    enableDetailPanel,
    initialState,
    onPaginationChange,
    onColumnFiltersChange,
    onSortingChange,
    totalRows,
    ...rest,
  });

  const [isColumnFilterOpen, setIsColumnFilterOpen] = useState(true);
  const customTableSettingsRef = useRef({
    disabledTopToolbar: slotProps?.topToolbar?.disabled,
  });
  // const shouldRender = useDelayedUnmount(
  //   isFetching && isLoading === false,
  //   4500,
  // );

  const { getTableProps, headerGroups, getTableBodyProps } = instance;

  const handleColumnFilterToggle = () => {
    setIsColumnFilterOpen((prev) => !prev);
  };

  useEffect(() => {
    customTableSettingsRef.current.disabledTopToolbar =
      slotProps?.topToolbar?.disabled;
  }, [slotProps?.topToolbar?.disabled]);

  useImperativeHandle(
    ref,
    () => ({
      getTableInstance: () => instance,
    }),
    [instance],
  );

  return (
    <>
      <TableTopToolbar
        instance={instance}
        openColumnFilter={isColumnFilterOpen}
        handleColumnFilterToggle={handleColumnFilterToggle}
        renderTopToolbar={renderTopToolbar}
        renderWithTopToolbar={renderWithTopToolbar}
        classNameTopToolbar={slotProps?.topToolbar?.className}
        disabledTopToolbar={customTableSettingsRef.current.disabledTopToolbar}
      />

      {/* <div className="tw-relative"> */}
      <div
        className={cn(
          "tw-relative tw-w-full tw-overflow-x-auto tw-border-t tw-border-0 tw-border-solid tw-border-gray-125",
          className,
        )}
      >
        <table
          className="tw-w-full tw-border-collapse tw-text-sm tw-caption-bottom tw-border-spacing-0"
          {...getTableProps()}
        >
          <thead className="tw-rounded-sm tw-sticky tw-top-0 tw-z-50 tw-border tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-gray-125 tw-shadow-md tw-align-top">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    className="tw-p-2 tw-text-base tw-relative tw-transition-all tw-ease-in-out tw-duration-300 tw-bg-white"
                    {...column.getHeaderProps()}
                  >
                    <div {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      <span>
                        {column.canSort && (
                          <SortArrow
                            isSorted={column.isSorted}
                            isSortedDesc={column.isSortedDesc}
                          />
                        )}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "tw-transform tw-duration-300 tw-ease-in-out tw-w-full",
                        isColumnFilterOpen
                          ? "tw-overflow-visible"
                          : "tw-overflow-hidden tw-hidden",
                      )}
                    >
                      {column.canFilter ? column.render("Filter") : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}

            <div
              className={cn("tw-h-1 tw-overflow-hidden tw-rounded-full", {
                "tw-bg-blue-200": isFetching && isLoading === false,
              })}
            >
              <div
                className={cn(
                  "tw-h-full tw-rounded-full tw-transition-all tw-duration-500",
                  {
                    "tw-bg-blue-400 tw-animate-progress":
                      isFetching && isLoading === false,
                  },
                )}
              />
            </div>
          </thead>

          <tbody {...getTableBodyProps()}>
            <DataTableBodyRows
              instance={instance}
              isLoading={isLoading}
              isNetworkRequestTakingTooLong={isNetworkRequestTakingTooLong}
              isError={isError}
              customErrorMessage={customErrorMessage}
              renderDetailPanel={renderDetailPanel}
              noDataMessage={slotProps?.body?.noDataMessage}
            />
          </tbody>
        </table>
        {/* </div> */}

        {/* <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full">
          {shouldRender && (
            <>
              <div class="tw-animate-car-move tw-text-4xl tw-rotate-180">
                <FaTruckMoving />
              </div>
              <div className="tw-bg-blue-200 tw-rounded-full tw-h-1" />
            </>
          )}
        </div> */}

        <div
          className={cn("tw-h-1 tw-overflow-hidden tw-rounded-full", {
            "tw-bg-blue-200": isFetching && isLoading === false,
          })}
        >
          <div
            className={cn(
              "tw-h-full tw-rounded-full tw-transition-all tw-duration-500",
              {
                "tw-bg-blue-400 tw-animate-progress":
                  isFetching && isLoading === false,
              },
            )}
          />
        </div>
      </div>

      <DefaultBottomToolbar
        instance={instance}
        enabledRowSelection={enableRowSelection}
      />
    </>
  );
}

const DataTableWithForwardRef = forwardRef(DataTable);

export default DataTableWithForwardRef;

/**
 * @template T
 * @typedef {Omit<import('react-table').TableOptions<T>, 'useControlledState' | 'stateReducer' | 'defaultColumn' | 'autoResetHiddenColumns'>} BaseTableOptions
 */

/**
 * @template T
 * @typedef {BaseTableOptions<T> & CustomTableProps<T>} DataTableProps
 */
